import React from 'react';

import { 
  Title, 
  Text, 
  List, 
  Code, 
  Blockquote, 
  Anchor, 
  Table, 
  Divider 
} from '@mantine/core';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ 
  content, 
  className 
}) => {
  return (
    <div className={className}>
      <ReactMarkdown
        components={{
          // Use Mantine Title components for headings
          h1: ({ children }) => (
            <Title mb="md" mt="lg" order={1}>
              {children}
            </Title>
          ),
          h2: ({ children }) => (
            <Title mb="sm" mt="md" order={2}>
              {children}
            </Title>
          ),
          h3: ({ children }) => (
            <Title mb="xs" mt="sm" order={3}>
              {children}
            </Title>
          ),
          h4: ({ children }) => (
            <Title mb="xs" mt="sm" order={4}>
              {children}
            </Title>
          ),
          h5: ({ children }) => (
            <Title mb="xs" mt="sm" order={5}>
              {children}
            </Title>
          ),
          h6: ({ children }) => (
            <Title mb="xs" mt="sm" order={6}>
              {children}
            </Title>
          ),
          
          // Use Mantine Text component for paragraphs
          p: ({ children }) => (
            <Text mb="md" size="md">
              {children}
            </Text>
          ),
          
          // Use Mantine List components
          ul: ({ children }) => (
            <List mb="md" size="md">
              {children}
            </List>
          ),
          ol: ({ children }) => (
            <List mb="md" size="md" type="ordered">
              {children}
            </List>
          ),
          li: ({ children }) => (
            <List.Item>
              {children}
            </List.Item>
          ),
          
          // Use Mantine Code component
          code: ({ children, className }) => {
            const isInline = !className;
            return isInline ? (
              <Code color="pink">
                {children}
              </Code>
            ) : (
              <Code block mb="md">
                {children}
              </Code>
            );
          },
          
          // Use Mantine Blockquote component
          blockquote: ({ children }) => (
            <Blockquote color="blue" mb="md">
              {children}
            </Blockquote>
          ),
          
          // Use Mantine Anchor component
          a: ({ children, href }) => (
            <Anchor href={href} rel="noopener noreferrer" target="_blank">
              {children}
            </Anchor>
          ),
          
          // Use Mantine Table components
          table: ({ children }) => (
            <Table highlightOnHover striped mb="md">
              {children}
            </Table>
          ),
          thead: ({ children }) => (
            <Table.Thead>
              {children}
            </Table.Thead>
          ),
          tbody: ({ children }) => (
            <Table.Tbody>
              {children}
            </Table.Tbody>
          ),
          tr: ({ children }) => (
            <Table.Tr>
              {children}
            </Table.Tr>
          ),
          th: ({ children }) => (
            <Table.Th>
              {children}
            </Table.Th>
          ),
          td: ({ children }) => (
            <Table.Td>
              {children}
            </Table.Td>
          ),
          
          // Use Mantine Divider component
          hr: () => (
            <Divider my="xl" />
          ),
          
          // Keep strong and emphasis as they work well with Mantine
          strong: ({ children }) => (
            <Text component="strong" fw={600}>
              {children}
            </Text>
          ),
          em: ({ children }) => (
            <Text component="em" fs="italic">
              {children}
            </Text>
          )
        }}
        remarkPlugins={[remarkBreaks]}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
